import React from 'react';

import { useIntl } from 'react-intl-next';

import { FilterProductIcon } from '../../../../../common/ui/filters/filter-product-icon';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import SelectFilter from '../../../../../common/ui/filters/select-filter';
import { formatAvatarOptionLabel } from '../../../../../common/ui/filters/select-filter/option-label';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { useFiltersDiscoverability2Enabled, usePrimaryProduct } from '../../../../../controllers/store/bootstrap';
import { filter as contributorFilter } from '../../../../../controllers/store/search/filters/contributor';

import { i18n } from './messages';

const ContributorFilter = ({
	isFilterInDropdown,
	showPrimaryProductIcon,
	fit = 'default',
	onFilterChange,
	hideOnHoverOut,
	onClearOptions,
}: FilterProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [primaryProduct] = usePrimaryProduct();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [
		filterData,
		{ loadInitialOptions, setLookupInput, setOption, clearLookupInput, clearOptions },
	] = useSelectFilter({
		filterId: contributorFilter.id,
	});

	if (!filterData) {
		return null;
	}

	const {
		isLoading,
		suggestedOptions,
		selectedOptions,
		options,
		isLookupDefined,
		lookupInput,
		isLookupActive,
	} = filterData;

	const groupedOptions = [
		{
			label: formatMessage(i18n.confluenceFilterContributorLabelSelected),
			options: selectedOptions,
		},
		{
			label: formatMessage(i18n.confluenceFilterContributorLabelUnselected),
			options: suggestedOptions,
		},
	];

	const label =
		fit === 'compact' && selectedOptions.length > 0 ? '' : formatMessage(i18n.contributor);

	return (
		<SelectFilter
			contentFit={fit}
			canClearFilter
			checkboxIndicators
			onDropdownOpen={loadInitialOptions}
			onDropdownClose={clearLookupInput}
			onSearchInputChange={setLookupInput}
			onSelectChange={(option, ...restArgs) => {
				setOption(option, ...restArgs);
				onFilterChange?.(option);
			}}
			options={isLookupActive ? options : groupedOptions}
			selectedOptions={selectedOptions}
			label={label}
			placeholder={formatMessage(i18n.confluenceFilterContributorPlaceholder)}
			filterOrControlId={'contributor'}
			isLoading={isLoading}
			isSearchable={isLookupDefined}
			searchInput={lookupInput}
			formatOptionLabel={formatAvatarOptionLabel}
			onClearOptions={() => {
				clearOptions();
				onClearOptions?.({ filterId: contributorFilter.id });
			}}
			isFilterInDropdown={isFilterInDropdown}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			iconBefore={
				<FilterProductIcon
					primaryProduct={primaryProduct}
					showPrimaryProductIcon={showPrimaryProductIcon}
				/>
			}
			hideOnHoverOut={hideOnHoverOut}
		/>
	);
};

export default ContributorFilter;
